/**
 * The browser half of a member reset, shared by "Start over" in Settings and
 * by the dev demo panel's reset actions. The server wipe is only half the job:
 * an explainer dismissal or a seen-toast id left behind makes a supposedly
 * first-run session skip its first-run copy.
 */

/**
 * Keys the reset must not touch. Everything here is a credential or a sign-in
 * pointer: clearing any of it logs the member out mid-reset and drops them on
 * the signed-out page instead of onboarding.
 *
 * `oidc.` is the oidc-client-ts namespace (both the user store and the PKCE
 * state store are configured onto localStorage in @healthos/auth).
 *
 * `clearAppLocalStorage({ includeAuth: true })` takes the credentials too,
 * which is what a reset that should end signed out needs. It still keeps the
 * embed channel: see CREDENTIAL_KEYS.
 * Pair it with a full page load: oidc-client-ts holds the user in memory as
 * well, so a client-side navigation would keep the session alive regardless.
 */
const AUTH_KEY_PREFIXES = ["oidc."];
const AUTH_KEYS = ["healthos:host-auth", "healthos.auth.returnTo", "thrive_dev_signed_in"];

/**
 * The subset `includeAuth` may take: the member's actual credentials. The rest
 * of AUTH_KEYS is transport, not identity. `healthos:host-auth` holds the embed
 * channel nonce and parent origin (never a token) and exists so a same-origin
 * full page load can recover a context the query string no longer carries,
 * which is exactly the navigation a signing-out reset performs. Clearing it
 * strands a desktop or Teams member on standalone sign-in inside the iframe.
 */
const CREDENTIAL_KEYS = ["thrive_dev_signed_in"];
const CREDENTIAL_KEY_PREFIXES = ["oidc."];

function isAuthKey(key: string): boolean {
  return AUTH_KEYS.includes(key) || AUTH_KEY_PREFIXES.some((p) => key.startsWith(p));
}

function isChannelKey(key: string): boolean {
  const credential =
    CREDENTIAL_KEYS.includes(key) || CREDENTIAL_KEY_PREFIXES.some((p) => key.startsWith(p));
  return isAuthKey(key) && !credential;
}

function clearStorage(store: Storage, keep: (key: string) => boolean): void {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && !keep(key)) doomed.push(key);
  }
  for (const key of doomed) store.removeItem(key);
}

/**
 * Empties browser storage so that after a reset the member sees a genuinely
 * fresh state: no cached onboarding progress, no dismissed explainers, no
 * seen-toast ids, no library or pulse visit flags, no saved grocery ticks.
 *
 * Deny-list rather than allow-list, deliberately. The previous version listed
 * the handful of keys someone remembered, which is how a "start over" ends up
 * carrying a dozen dismissals into a first-run session every time a feature
 * adds a key. Anything new is cleared by default; a key that must survive has
 * to say so in AUTH_KEYS above.
 */
export function clearAppLocalStorage(options: { includeAuth?: boolean } = {}): void {
  const keep = options.includeAuth ? isChannelKey : isAuthKey;

  try {
    clearStorage(localStorage, keep);
  } catch {
    /* localStorage unavailable or sandboxed */
  }

  try {
    clearStorage(sessionStorage, keep);
  } catch {
    /* sessionStorage unavailable */
  }
}
